import React, { useCallback } from 'react';
import { Autocomplete, Grid2, IconButton, TextField, Tooltip } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { MODAL_TYPE } from '../../hooks/useModal';
import { CUSTOM_FIELDS_TYPES } from '../../helpers/constants';
import TranslatableTextField from '../atom/TranslatableTextField';
import UploadImageInput from '../atom/UploadImageInput';

/**
 * Basic info section - handles title, subheadline, tags, and images
 * Significantly cleaner than inline implementation
 */
const ShowBasicInfoSection = React.memo(({ values, setFieldValue, touched, errors, showModal }) => {
    const { t } = useTranslation();

    const updateMarketing = useCallback(
        (field, value) => {
            setFieldValue('marketing', { ...values.marketing, [field]: value });
        },
        [values.marketing, setFieldValue],
    );

    return (
        <>
            {/* Title, Subtitle - same row, equal width */}
            <TranslatableTextField
                gridSize={{ xs: 12, sm: 6 }}
                value={values.title}
                onChange={(ev) => setFieldValue('title', ev.target.value)}
                onOpenTranslations={() =>
                    showModal(MODAL_TYPE.TRANSLATIONS, {
                        translations: values.titleI18n,
                        setTranslations: (translations) => setFieldValue('titleI18n', translations),
                        item: 'shows',
                        column: 'title',
                        venueId: values.venueId,
                        originalString: values.title,
                    })
                }
                label={t('shows.columns.title')}
                error={touched.title && Boolean(errors.title)}
                helperText={touched.title ? errors.title : ''}
                required
            />
            <TranslatableTextField
                gridSize={{ xs: 12, sm: 6 }}
                value={values.subtitle || ''}
                onChange={(ev) => setFieldValue('subtitle', ev.target.value)}
                onOpenTranslations={() =>
                    showModal(MODAL_TYPE.TRANSLATIONS, {
                        translations: values.subtitleI18n,
                        setTranslations: (translations) =>
                            setFieldValue('subtitleI18n', translations),
                        item: 'shows',
                        column: 'subtitle',
                        venueId: values.venueId,
                        originalString: values.subtitle,
                    })
                }
                label={t('shows.columns.subtitle')}
                error={touched.subtitle && Boolean(errors.subtitle)}
                helperText={touched.subtitle ? errors.subtitle : ''}
                disabled={!values.venueId}
            />

            {/* Subheadline - separate row, same full width */}
            <TranslatableTextField
                gridSize={{ xs: 12 }}
                value={values.marketing.subheadline || ''}
                onChange={(e) => updateMarketing('subheadline', e.target.value)}
                onOpenTranslations={() =>
                    showModal(MODAL_TYPE.TRANSLATIONS, {
                        translations: values.marketing.subheadlineI18n,
                        setTranslations: (subheadlineI18n) =>
                            updateMarketing('subheadlineI18n', subheadlineI18n),
                        item: 'shows',
                        column: 'marketing.subheadline',
                        intermediateColumn: 'columns',
                        venueId: values.venueId,
                        originalString: values.marketing.subheadline,
                    })
                }
                label={t('shows.columns.marketing.subheadline')}
                disabled={!values.venueId}
            />

            {/* Tags */}
            <Grid2 size={{ xs: 10, sm: 11 }}>
                <Autocomplete
                    multiple
                    fullWidth
                    freeSolo
                    options={[]}
                    value={values.marketing.tags || []}
                    onChange={(_, newValue) =>
                        updateMarketing(
                            'tags',
                            newValue.filter((tag) => tag.trim()),
                        )
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('shows.columns.marketing.tags')}
                            placeholder={t('common.freesoloAutocompletePlaceholder')}
                            fullWidth
                        />
                    )}
                    getOptionLabel={(option) => `${option}`}
                />
            </Grid2>
            <Grid2
                size={{ xs: 2, sm: 1 }}
                sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}
            >
                <Tooltip title={t('common.translations')}>
                    <IconButton
                        onClick={() =>
                            showModal(MODAL_TYPE.TRANSLATIONS, {
                                translations: values.marketing.tagsI18n,
                                setTranslations: (tagsI18n) =>
                                    updateMarketing('tagsI18n', tagsI18n),
                                item: 'shows',
                                column: 'marketing.tags',
                                intermediateColumn: 'columns',
                                venueId: values.venueId,
                                originalString: values.marketing.tags,
                                type: CUSTOM_FIELDS_TYPES.DROPDOWN,
                                originalData: values.marketing.tags,
                            })
                        }
                        disabled={!values.venueId}
                        size="small"
                        edge="end"
                    >
                        <LanguageIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Grid2>

            {/* Logo */}
            <Grid2 size={{ xs: 10, sm: 11 }}>
                <UploadImageInput
                    value={values.marketing.logo}
                    setFieldValue={(value) => updateMarketing('logo', value)}
                    title="shows.columns.marketing.logo"
                />
            </Grid2>
            <Grid2
                size={{ xs: 2, sm: 1 }}
                sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}
            >
                <Tooltip title={t('common.translations')}>
                    <IconButton
                        onClick={() =>
                            showModal(MODAL_TYPE.IMAGE_TRANSLATIONS, {
                                translations: values.marketing.logoI18n,
                                setTranslations: (logoI18n) =>
                                    updateMarketing('logoI18n', logoI18n),
                                item: 'shows',
                                column: 'marketing.logo',
                                intermediateColumn: 'columns',
                                venueId: values.venueId,
                                originalString: values.marketing.logo,
                                type: CUSTOM_FIELDS_TYPES.SINGLE,
                            })
                        }
                        disabled={!values.venueId}
                        size="small"
                        edge="end"
                    >
                        <LanguageIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Grid2>

            {/* Primary Image */}
            <Grid2 size={{ xs: 10, sm: 11 }}>
                <UploadImageInput
                    value={values.marketing.primaryImage}
                    setFieldValue={(value) => updateMarketing('primaryImage', value)}
                    title="shows.columns.marketing.primaryImage"
                />
            </Grid2>
            <Grid2
                size={{ xs: 2, sm: 1 }}
                sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}
            >
                <Tooltip title={t('common.translations')}>
                    <IconButton
                        onClick={() =>
                            showModal(MODAL_TYPE.IMAGE_TRANSLATIONS, {
                                translations: values.marketing.primaryImageI18n,
                                setTranslations: (primaryImageI18n) =>
                                    updateMarketing('primaryImageI18n', primaryImageI18n),
                                item: 'shows',
                                column: 'marketing.primaryImage',
                                intermediateColumn: 'columns',
                                venueId: values.venueId,
                                originalString: values.marketing.primaryImage,
                                type: CUSTOM_FIELDS_TYPES.SINGLE,
                            })
                        }
                        disabled={!values.venueId}
                        size="small"
                        edge="end"
                    >
                        <LanguageIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Grid2>

            {/* Extra Images */}
            <Grid2 size={{ xs: 10, sm: 11 }}>
                <UploadImageInput
                    multiple
                    value={values.marketing.extraImages}
                    setFieldValue={(newImages) => updateMarketing('extraImages', newImages)}
                    title="shows.columns.marketing.extraImages"
                />
            </Grid2>
            <Grid2
                size={{ xs: 2, sm: 1 }}
                sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}
            >
                <Tooltip title={t('common.translations')}>
                    <IconButton
                        onClick={() =>
                            showModal(MODAL_TYPE.IMAGE_TRANSLATIONS, {
                                translations: values.marketing.extraImagesI18n,
                                setTranslations: (extraImagesI18n) =>
                                    updateMarketing('extraImagesI18n', extraImagesI18n),
                                item: 'shows',
                                column: 'marketing.extraImages',
                                intermediateColumn: 'columns',
                                venueId: values.venueId,
                                originalString: values.marketing.extraImages,
                                type: CUSTOM_FIELDS_TYPES.MULTI,
                            })
                        }
                        disabled={!values.venueId}
                        size="small"
                        edge="end"
                    >
                        <LanguageIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Grid2>
        </>
    );
});

ShowBasicInfoSection.displayName = 'ShowBasicInfoSection';

export default ShowBasicInfoSection;
